import React, { FC } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import ActionSpinner from '../custom/components/ActionSpinner';
import usePermissions from '../hooks/usePermissions';
import { getRoutePermission, requiresHierarchicalPermission } from './routePermissions';

interface IProtectedRouteProps {
  requiredPermission?: string;
  parentPermission?: string;
  children?: React.ReactNode;
}

const ProtectedRoute: FC<IProtectedRouteProps> = ({ 
  requiredPermission, 
  parentPermission,
  children 
}) => {
  const { hasPermission, hasHierarchicalPermission, isLoading } = usePermissions();
  const location = useLocation();

  if (isLoading) {
    return <ActionSpinner />;
  }

  // Get route permission configuration
  const routePermission = getRoutePermission(location.pathname);
  
  // Determine which permissions to check
  let childPerm = requiredPermission;
  let parentPerm = parentPermission;
  
  // If route permission mapping exists, use those values
  if (routePermission) {
    childPerm = childPerm || routePermission.childPermission;
    parentPerm = parentPerm || routePermission.parentPermission;
  }

  // If no permission specified, allow access
  if (!childPerm) {
    return children ? <>{children}</> : <Outlet />;
  }

  // Check permissions based on whether hierarchical validation is needed
  let hasAccess = false;

  if (parentPerm) {
    // Use hierarchical permission checking
    hasAccess = hasHierarchicalPermission(childPerm, parentPerm);
  } else if (requiresHierarchicalPermission(location.pathname)) {
    // Route requires hierarchical validation but no parent specified
    // This is a configuration error - deny access for security
    hasAccess = false;
  } else {
    // Use regular permission checking
    hasAccess = hasPermission(childPerm);
  }

  if (!hasAccess) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children ? <>{children}</> : <Outlet />;
};

export default ProtectedRoute;